import type { CatalogueFieldConfig } from '../componentFormTypes';

/**
 * Phase 6e2b-1 — §51.2 per-type accessory spec table as literal config
 * objects (the legacy accessories/<type>/view/form.js constructor args,
 * extracted verbatim from the worktree source — current with origin/dev).
 *
 * 6e2b-1 scope: the 10 simpler types. NOT here (6e2b-2): access_door,
 * opening, uvc, louver, hood, damper, ams — the chassis carries their
 * extension points (catalogueFieldsIntake swap, IW dataGetter, autoSize,
 * hood↔damper web) as config seams, not implementations.
 *
 * Per-type model layer = these plurals, nothing more (parity 343 /
 * [NEG]#113 — no legacy model blobs resurrected).
 */

export type AccessoryTypeName =
  | 'light'
  | 'jbox'
  | 'disconnect'
  | 'enclosure'
  | 'adp_gage'
  | 'vfd'
  | 'dog_house'
  | 'starter'
  | 'drain_pan'
  | 'user_defined_accessory'
  // ---- 6e2b-2 complex types ----
  | 'access_door'
  | 'opening'
  | 'uvc'
  | 'louver'
  | 'hood'
  | 'damper'
  | 'ams';

export interface AccessoryTypeConfig {
  type: AccessoryTypeName;
  /** REST plural (urlRoot of accessories/<type>/model/<type>.js). */
  plural: string;
  /** storage classID — `model.accessory.<type>` (§49.6). */
  classID: string;
  /**
   * Per-type CMS params doc (fn['current.cms.<type>'] → model urlRoot) +
   * the unwrap key (`cms_concrete_accessory.get(type)`; dog_house passes the
   * WHOLE doc — tab.accessories.js:349; uda has none — map.js commented out).
   */
  cmsAccessoryURL?: string;
  /** unwrap key into the doc; null = whole doc (dog_house). */
  cmsAccessoryKey?: string | null;
  catalogueFields: CatalogueFieldConfig[];
  /**
   * 6e2b-2 — damper alternate field set when mounted on fan_intake
   * (accessories/damper/view/form.js catalogueFieldsIntake; §49.6:200-207).
   */
  catalogueFieldsIntake?: CatalogueFieldConfig[];
  /** form.js subtabs array verbatim (tmpl data-subtab census can differ —
   * dead keys are harmless: adp_gage tmpl uses 'gage', config says
   * 'properties'; jbox config lists 'remarks' with no tmpl input). */
  subtabs: string[];
  withPosition: boolean;
  loadPositionWhenAlreadySelected?: boolean;
  /** setupMounting (§49.6) — absent = no mounting view at all (uda). */
  setupMounting?: { withPopup: boolean };
  validatedFields: string[];
  /** §49.6 auto-position exemption list (uda/uvc/drain_pan/access_door/starter). */
  hasAutoPosition: boolean;
}

/** Shared §51.2 mounting/position validatedFields block (clones). */
const POSITION_VALIDATED = [
  'mounting_plane_id',
  'mounting_id',
  'position_y',
  'position_Wy',
  'position_x',
  'position_Lx',
  'casing_mount_id',
  'distance_to_floor',
  'distance_to_roof',
  'distance_to_left_wall',
  'distance_to_right_wall',
  'distance_to_upstream_wall',
  'distance_to_downstream_wall',
];

export const ACCESSORY_FORM_CONFIGS: Record<AccessoryTypeName, AccessoryTypeConfig> = {
  light: {
    type: 'light',
    plural: 'lights',
    classID: 'model.accessory.light',
    cmsAccessoryURL: '/api/v1.0/cms/1/v1.0.0/ahu/lights/params/',
    cmsAccessoryKey: 'light',
    catalogueFields: [
      {
        targetField: 'master_light_id',
        paramsSourceField: 'light_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/lights/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/lights/',
        componentName: 'light',
        componentsName: 'lights',
        paramsFields: ['mfg_id', 'model', 'type_id', 'wattage', 'series_id', 'style_id', 'voltage_id'],
        accessoryResetExtras: true,
      },
      {
        targetField: 'master_switch_id',
        paramsSourceField: 'switch_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/lights/switches/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/lights/switches/',
        componentName: 'switch',
        componentsName: 'switches',
        paramsFields: ['mfg_id', 'model', 'type'],
        // switch is in the §49.4 reset-exemption list — no canvas removal.
      },
    ],
    subtabs: ['properties', 'electrical', 'mounting', 'switch'],
    withPosition: true,
    setupMounting: { withPopup: true },
    validatedFields: [
      'master_light_id',
      'master_switch_id',
      'mfg_id',
      'mounting_plane_id',
      'mounting_id',
      'position_y',
      'position_Wy',
      'position_x',
      'position_Lx',
      'distance_to_floor',
      'distance_to_roof',
      'distance_to_left_wall',
      'distance_to_right_wall',
      'distance_to_upstream_wall',
      'distance_to_downstream_wall',
      'orientation_id',
    ],
    hasAutoPosition: true,
  },

  jbox: {
    type: 'jbox',
    plural: 'jboxes',
    classID: 'model.accessory.jbox',
    cmsAccessoryURL: '/api/v1.0/cms/1/v1.0.0/ahu/jboxes/params/',
    cmsAccessoryKey: 'jbox',
    catalogueFields: [
      {
        targetField: 'master_jbox_id',
        paramsSourceField: 'jbox_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/jboxes/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/jboxes/',
        componentName: 'jbox',
        componentsName: 'jboxes',
        paramsFields: ['mfg_id', 'mfg_name', 'model', 'width', 'height', 'nema_encl_type'],
        accessoryResetExtras: true,
      },
    ],
    subtabs: ['properties', 'mounting', 'remarks'],
    withPosition: true,
    setupMounting: { withPopup: true },
    validatedFields: ['master_jbox_id', ...POSITION_VALIDATED],
    hasAutoPosition: true,
  },

  disconnect: {
    type: 'disconnect',
    plural: 'disconnects',
    classID: 'model.accessory.disconnect',
    cmsAccessoryURL: '/api/v1.0/cms/1/v1.0.0/ahu/disconnects/params/',
    cmsAccessoryKey: 'disconnect',
    catalogueFields: [
      {
        targetField: 'master_disconnect_id',
        paramsSourceField: 'disconnect_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/disconnects/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/disconnects/',
        componentName: 'disconnect',
        componentsName: 'disconnects',
        paramsFields: [
          'mfg_id',
          'series_id',
          'nema_encl_type_id',
          'model',
          'amp_rating',
          'id',
          'mfg_name',
          'series',
          'nema_encl_type_name',
        ],
        accessoryResetExtras: true,
      },
    ],
    subtabs: ['properties', 'mounting'],
    withPosition: true,
    setupMounting: { withPopup: true },
    validatedFields: ['master_disconnect_id', ...POSITION_VALIDATED],
    hasAutoPosition: true,
  },

  enclosure: {
    type: 'enclosure',
    plural: 'enclosures',
    classID: 'model.accessory.enclosure',
    cmsAccessoryURL: '/api/v1.0/cms/1/v1.0.0/ahu/enclosures/params/',
    cmsAccessoryKey: 'enclosure',
    catalogueFields: [
      {
        targetField: 'master_enclosure_id',
        paramsSourceField: 'enclosure_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/enclosures/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/enclosures/',
        componentName: 'enclosure',
        componentsName: 'enclosures',
        paramsFields: [
          'mfg_id',
          'mfg_name',
          'series_name',
          'model',
          'nema_encl_type',
          'door_hinge_side_id',
          'door_configuration',
          'mtl_id',
          'width',
          'height',
          'depth',
        ],
        accessoryResetExtras: true,
      },
    ],
    subtabs: ['properties', 'mounting'],
    withPosition: true,
    setupMounting: { withPopup: true },
    validatedFields: ['master_enclosure_id', ...POSITION_VALIDATED],
    hasAutoPosition: true,
  },

  adp_gage: {
    type: 'adp_gage',
    plural: 'adp_gages',
    classID: 'model.accessory.adp_gage',
    cmsAccessoryURL: '/api/v1.0/cms/1/v1.0.0/ahu/adp_gages/params/',
    cmsAccessoryKey: 'adp_gage',
    catalogueFields: [
      {
        targetField: 'master_adp_gage_id',
        paramsSourceField: 'adp_gage_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/adp_gages/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/adp_gages/',
        componentName: 'adp_gage',
        componentsName: 'adp_gages',
        paramsFields: ['mfg_id', 'model'],
        accessoryResetExtras: true,
      },
    ],
    subtabs: ['properties', 'mounting', 'remarks'],
    withPosition: true,
    loadPositionWhenAlreadySelected: true,
    setupMounting: { withPopup: true },
    validatedFields: ['master_adp_gage_id', ...POSITION_VALIDATED],
    hasAutoPosition: true,
  },

  vfd: {
    type: 'vfd',
    plural: 'vfds',
    classID: 'model.accessory.vfd',
    cmsAccessoryURL: '/api/v1.0/cms/1/v1.0.0/ahu/motor_controllers/vfd/params/',
    cmsAccessoryKey: 'vfd',
    catalogueFields: [
      {
        targetField: 'master_vfd_id',
        paramsSourceField: 'vfd_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/motor_controllers/vfd/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/motor_controllers/vfd/',
        componentName: 'vfd',
        componentsName: 'vfds',
        paramsFields: [
          'mfg_id',
          'series_id',
          'model',
          'description_id',
          'nema_encl_type_id',
          'power',
          'power_id',
          'voltage',
          'voltage_id',
          'fla',
          'mfg_name',
          'series_name',
          'nema_encl_type_name',
        ],
        // dev accessories/vfd/view/form.js:60 — the read-only viewer has no
        // count badge, so the count fetches are skipped (catalogueField.ts:208).
        noFetchCount: true,
        accessoryResetExtras: true,
      },
    ],
    subtabs: ['properties', 'mounting'],
    withPosition: true,
    setupMounting: { withPopup: true },
    validatedFields: ['master_vfd_id', 'housed_in_enclosure', 'width', 'height', ...POSITION_VALIDATED],
    hasAutoPosition: true,
  },

  dog_house: {
    type: 'dog_house',
    plural: 'dog_houses',
    classID: 'model.accessory.dog_house',
    // SINGULAR route (dog_house.cms.js:7) + WHOLE-doc cms_accessory
    // (tab.accessories.js:349 `cms_concrete_accessory.toJSON()`).
    cmsAccessoryURL: '/api/v1.0/cms/1/v1.0.0/ahu/dog_house/params/',
    cmsAccessoryKey: null,
    catalogueFields: [],
    subtabs: ['properties', 'mounting'],
    withPosition: true,
    loadPositionWhenAlreadySelected: true,
    setupMounting: { withPopup: true },
    validatedFields: ['master_dog_house_id', ...POSITION_VALIDATED],
    hasAutoPosition: true,
  },

  starter: {
    type: 'starter',
    plural: 'starters',
    classID: 'model.accessory.starter',
    cmsAccessoryURL: '/api/v1.0/cms/1/v1.0.0/ahu/starter/params/',
    cmsAccessoryKey: 'starter',
    catalogueFields: [
      {
        targetField: 'master_starter_contactor_id',
        paramsSourceField: 'starter_contactor_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/starter/contactors/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/starter/contactors/',
        componentName: 'contactor',
        componentsName: 'contactors',
        paramsFields: ['mfg_id', 'model', 'rated_power', 'rated_op_voltage', 'rated_op_current'],
        // contactor is reset-EXEMPT (§49.4 :616 / parity 312).
      },
      {
        targetField: 'master_starter_overload_id',
        paramsSourceField: 'starter_overload_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/starter/overloads/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/starter/overloads/',
        componentName: 'overload',
        componentsName: 'overloads',
        paramsFields: ['mfg_id', 'model', 'rated_op_voltage', 'amp_min', 'amp_max'],
        // overload is reset-EXEMPT.
      },
    ],
    subtabs: ['contactor', 'overload', 'mounting'],
    // starter has NO position (auto-position exemption list §49.6:306) and an
    // INLINE mounting panel (withPopup:false — mounting_view renders into the
    // form container via stateChangedForRendering).
    withPosition: false,
    setupMounting: { withPopup: false },
    validatedFields: ['master_starter_contactor_id', 'master_starter_overload_id'],
    hasAutoPosition: false,
  },

  drain_pan: {
    type: 'drain_pan',
    plural: 'drain_pans',
    classID: 'model.accessory.drain_pan',
    // dev re-scoped params/ to REQUIRE ?type_id (drain_pans_viewset.py:67-70 —
    // 400 without it). The base URL is therefore EMPTY: the form has no CMS doc
    // until a Type is picked; DrainPanForm drives the per-type re-fetch via
    // setCmsURLOverride('…/drain_pan/params/?type_id=<id>') on change of
    // drain_pan_type_id (legacy DrainPanView listenTo change:drain_pan_type_id).
    cmsAccessoryKey: 'drain_pan',
    catalogueFields: [],
    subtabs: ['properties', 'remarks', 'mounting'],
    // NO position at all — drawDrainPanOnCanvas path (§49.6/§51.2).
    withPosition: false,
    // legacy DrainPanView.validatedFields (form.js:23-31): dim1_length,
    // distance_from_section_ups, conn_hand_id, conn_pos_id.
    validatedFields: ['dim1_length', 'distance_from_section_ups', 'conn_hand_id', 'conn_pos_id'],
    hasAutoPosition: false,
  },

  // -------------------------------------------------------------------------
  // 6e2b-2 — the 7 COMPLEX types (per-type form.js configs verbatim)
  // -------------------------------------------------------------------------

  access_door: {
    type: 'access_door',
    plural: 'access_doors',
    classID: 'model.accessory.access_door',
    cmsAccessoryURL: '/api/v1.0/cms/1/v1.0.0/ahu/access_door/params/',
    cmsAccessoryKey: 'access_door',
    catalogueFields: [
      {
        targetField: 'master_viewport_id',
        paramsSourceField: 'viewport_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/access_door/viewports/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/access_door/viewports/',
        componentName: 'viewport',
        componentsName: 'viewports',
        paramsFields: ['mfg_id', 'shape_id', 'seal_id', 'model', 'size'],
        // viewport is reset-EXEMPT (§49.4 :616 / parity 312) — no
        // accessoryResetExtras, no remove_accessory_from_canvas.
      },
    ],
    subtabs: ['properties', 'mounting', 'viewport'],
    withPosition: true,
    setupMounting: { withPopup: true },
    validatedFields: [
      'master_viewport_id',
      'inside_liner_mtl_gauge_id',
      'width',
      'height',
      'mounting_plane_id',
      // legacy list carries a literal "'              distance_to_floor'"
      // typo'd entry — the broken key never matches a change; live keys:
      'distance_to_roof',
      'distance_to_left_wall',
      'distance_to_right_wall',
      'distance_to_upstream_wall',
      'distance_to_downstream_wall',
      'distance_to_btm_edge',
      'distance_to_ups_edge',
      'distance_to_left_edge',
      'position_y',
      'position_Wy',
      'position_x',
      'position_Lx',
    ],
    // §49.6 auto-position exemption list includes access_door.
    hasAutoPosition: false,
  },

  opening: {
    type: 'opening',
    plural: 'openings',
    classID: 'model.accessory.opening',
    cmsAccessoryURL: '/api/v1.0/cms/1/v1.0.0/ahu/opening/params/',
    cmsAccessoryKey: 'opening',
    catalogueFields: [],
    // form.js says ['properties','mounting','viewport'] — 'viewport' is a
    // dead config key; the tmpl's third subtab input is 'performance'.
    subtabs: ['properties', 'mounting', 'viewport'],
    withPosition: true,
    loadPositionWhenAlreadySelected: true,
    setupMounting: { withPopup: true },
    validatedFields: [
      'shape_id',
      'width',
      'height',
      'mounting_plane_id',
      'mounting_id',
      'distance_to_floor',
      'distance_to_roof',
      'distance_to_left_wall',
      'distance_to_right_wall',
      'distance_to_upstream_wall',
      'distance_to_downstream_wall',
      'hinge_side_id',
      'swing_id',
      'position_y',
      'position_Wy',
      'position_x',
      'position_Lx',
      'face_velocity_actual',
      'apd',
      'airflow_actual',
    ],
    hasAutoPosition: true,
  },

  uvc: {
    type: 'uvc',
    plural: 'uvcs',
    // EXACT classID — the §49.2 auto-save guard keys off it (uvc never
    // auto-saves; parity 300/327).
    classID: 'model.accessory.uvc',
    cmsAccessoryURL: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_params/',
    cmsAccessoryKey: 'uvc',
    // §50.1 data_uvc 13-sub-catalog field flattened to one engine per
    // sub-catalog (the legacy ctor loops data_uvc[] building one CMSPages
    // each). NOTE the [NEG] lamp_params collision: current_sensor REUSES
    // paramsSourceField 'lamp_params' — replicated as-is (parity 327).
    catalogueFields: [
      {
        targetField: 'power_supply_id',
        paramsSourceField: 'power_supply_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_power_supply/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_power_supply/',
        componentName: 'power_supply',
        componentsName: 'power_supplies',
        paramsFields: ['series_id', 'voltage_id'],
        noFetchCount: true,
      },
      {
        targetField: 'lamp_id',
        paramsSourceField: 'lamp_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_lamp/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_lamp/',
        componentName: 'lamp',
        componentsName: 'lamps',
        paramsFields: ['series_id', 'lamp_output_id', 'lamp_finish_id'],
        noFetchCount: true,
      },
      {
        targetField: 'uvc_current_sensor_id',
        paramsSourceField: 'lamp_params', // [NEG] collision — verbatim
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_current_sensor/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_current_sensor/',
        componentName: 'current_sensor',
        componentsName: 'current_sensors',
        paramsFields: ['model'],
        noFetchCount: true,
      },
      {
        targetField: 'uvc_uv_repeat_kit_id',
        paramsSourceField: 'uvc_uv_repeat_kit_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_uv_repeat_kit/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_uv_repeat_kit/',
        componentName: 'uvc_uv_repeat_kit',
        componentsName: 'uvc_uv_repeat_kits',
        paramsFields: ['model'],
        noFetchCount: true,
      },
      {
        targetField: 'uvc_lamp_holder_id',
        paramsSourceField: 'uvc_lamp_holder_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_lamp_holder/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_lamp_holder/',
        componentName: 'uvc_lamp_holder',
        componentsName: 'uvc_lamp_holders',
        paramsFields: ['series_id'],
        noFetchCount: true,
      },
      {
        targetField: 'uvc_balast_connector_kit_id',
        paramsSourceField: 'uvc_balast_connector_kit_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_balast_connector_kit/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_balast_connector_kit/',
        componentName: 'uvc_balast_connector_kit',
        componentsName: 'uvc_balast_connector_kits',
        paramsFields: ['model'],
        noFetchCount: true,
      },
      {
        targetField: 'uvc_frameing_kit_id',
        paramsSourceField: 'uvc_frameing_kit_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_frameing_kit/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_frameing_kit/',
        componentName: 'uvc_frameing_kit',
        componentsName: 'uvc_frameing_kits',
        paramsFields: ['series_id'],
        noFetchCount: true,
      },
      {
        targetField: 'uvc_radio_meter_id',
        paramsSourceField: 'uvc_radio_meter_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_radio_meter/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_radio_meter/',
        componentName: 'uvc_radio_meter',
        componentsName: 'uvc_radio_meters',
        paramsFields: ['model'],
        noFetchCount: true,
      },
      {
        targetField: 'uvc_hour_meter_id',
        paramsSourceField: 'uvc_hour_meter_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_hour_meter/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_hour_meter/',
        componentName: 'uvc_hour_meter',
        componentsName: 'uvc_hour_meters',
        paramsFields: ['model'],
        noFetchCount: true,
      },
      {
        targetField: 'uvc_inter_lock_switch_id',
        paramsSourceField: 'uvc_inter_lock_switch_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_inter_lock_switch/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_inter_lock_switch/',
        componentName: 'uvc_inter_lock_switch',
        componentsName: 'uvc_inter_lock_switchs',
        paramsFields: ['model'],
        noFetchCount: true,
      },
      {
        targetField: 'uvc_toggle_switch_id',
        paramsSourceField: 'uvc_toggle_switch_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_toggle_switch/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_toggle_switch/',
        componentName: 'uvc_toggle_switch',
        componentsName: 'uvc_toggle_switchs',
        paramsFields: ['model'],
        noFetchCount: true,
      },
      {
        targetField: 'uvc_warning_sign_id',
        paramsSourceField: 'uvc_warning_sign_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_warning_sign/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_warning_sign/',
        componentName: 'uvc_warning_sign',
        componentsName: 'uvc_warning_signs',
        paramsFields: ['model'],
        noFetchCount: true,
      },
      {
        targetField: 'uvc_intlk_contact_controller_id',
        paramsSourceField: 'uvc_intlk_contact_controller_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_intlk_contact_controller/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/uvc_intlk_contact_controller/',
        componentName: 'uvc_intlk_contact_controller',
        componentsName: 'uvc_intlk_contact_controllers',
        paramsFields: ['model'],
        noFetchCount: true,
      },
    ],
    subtabs: ['properties', 'power_supply', 'lamp'],
    // uvc form.js sets NEITHER withPosition NOR setupMounting (the §51.2
    // "mounting popup yes" row describes the SELECTOR-embedded popup, not
    // the form) — and uvc is in the no-auto-position list.
    withPosition: false,
    validatedFields: [],
    hasAutoPosition: false,
  },

  louver: {
    type: 'louver',
    plural: 'louvers',
    classID: 'model.accessory.louver',
    cmsAccessoryURL: '/api/v1.0/cms/1/v1.0.0/ahu/louvers/params/',
    cmsAccessoryKey: 'louver',
    catalogueFields: [
      {
        targetField: 'master_louver_id',
        paramsSourceField: 'louver_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/louvers/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/louvers/',
        componentName: 'louver',
        componentsName: 'louvers',
        paramsFields: [
          'mfg_id',
          'series_id',
          'frame_mtl_id',
          'aw_width',
          'aw_height',
          'mfg_name',
          'series_name',
        ],
        noFetchCount: true,
        accessoryResetExtras: true,
      },
    ],
    subtabs: ['properties', 'mounting', 'performance'],
    withPosition: true,
    setupMounting: { withPopup: true },
    validatedFields: [
      'master_louver_id',
      'mounting_plane_id',
      'mounting_id',
      'distance_to_floor',
      'distance_to_roof',
      'distance_to_left_wall',
      'distance_to_right_wall',
      'distance_to_upstream_wall',
      'distance_to_downstream_wall',
      'position_y',
      'position_Wy',
      'position_x',
      'position_Lx',
      'casing_mount_id',
      'blade_direction_id',
      'face_velocity_actual',
      'apd',
      'airflow_actual',
    ],
    hasAutoPosition: true,
  },

  hood: {
    type: 'hood',
    plural: 'hoods',
    classID: 'model.accessory.hood',
    cmsAccessoryURL: '/api/v1.0/cms/1/v1.0.0/ahu/hoods/params/',
    cmsAccessoryKey: 'hood',
    catalogueFields: [],
    subtabs: ['properties', 'mounting', 'performance'],
    withPosition: true,
    setupMounting: { withPopup: true },
    validatedFields: [
      'style_id',
      'width',
      'height',
      'height_2',
      'depth',
      'depth_mode_id',
      'airflow_face_id',
      'angle_1',
      'angle_2',
      'angle_3',
      'angle_4',
      'mounting_plane_id',
      'mounting_id',
      'hinge_side_id',
      'swing_id',
      'position_y',
      'position_Wy',
      'position_x',
      'position_Lx',
      'face_velocity',
      'distance_to_floor',
      'distance_to_roof',
      'distance_to_left_wall',
      'distance_to_right_wall',
      'distance_to_section_ups_edge',
      'distance_to_section_downs_edge',
      'airflow_actual',
    ],
    hasAutoPosition: true,
  },

  damper: {
    type: 'damper',
    plural: 'dampers',
    classID: 'model.accessory.damper',
    cmsAccessoryURL: '/api/v1.0/cms/1/v1.0.0/ahu/dampers/params/',
    cmsAccessoryKey: 'damper',
    catalogueFields: [
      {
        targetField: 'master_damper_id',
        paramsSourceField: 'damper_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/dampers/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/dampers/',
        componentName: 'damper',
        componentsName: 'dampers',
        noFetchCount: true,
        paramsFields: [
          'mfg_id',
          'series_id',
          'blade_orientation_id',
          'frame_mtl_id',
          'shaft_mtl_id',
          'leakage_id',
          'aw_width',
          'aw_height',
          'mfg_name',
          'series_name',
        ],
        accessoryResetExtras: true,
      },
    ],
    catalogueFieldsIntake: [
      {
        targetField: 'master_damper_id',
        paramsSourceField: 'damper_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/fans/dampers/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/fans/dampers/',
        componentName: 'damper',
        componentsName: 'dampers',
        paramsFields: [
          'mfg_id',
          'series_id',
          'blade_orientation_id',
          'frame_mtl_id',
          'shaft_mtl_id',
          'leakage_id',
          'aw_width',
          'aw_height',
        ],
        accessoryResetExtras: true,
      },
    ],
    subtabs: ['properties', 'mounting', 'performance'],
    withPosition: true,
    loadPositionWhenAlreadySelected: true,
    setupMounting: { withPopup: true },
    validatedFields: [
      'master_damper_id',
      'mounting_plane_id',
      'mounting_id',
      'hinge_side_id',
      'distance_to_floor',
      'distance_to_roof',
      'distance_to_left_wall',
      'distance_to_right_wall',
      'distance_to_upstream_wall',
      'distance_to_downstream_wall',
      'distance_to_btm_edge',
      'distance_to_ups_edge',
      'distance_to_left_edge',
      'swing_id',
      'position_y',
      'position_Wy',
      'position_x',
      'position_Lx',
      'face_velocity_actual',
      'airflow_actual',
      'apd',
      'casing_mount_id',
      'blade_direction_id',
      'x',
      'y',
    ],
    hasAutoPosition: true,
  },

  // ams — LEGACY Backbone.View pattern; STRUCTURAL port only. NOTE: the live
  // tree cannot construct it (no app.js alias `view.accessory.ams.form`, no
  // accessories/ams/tmpl/ directory on disk) and dev3 has zero ams rows.
  ams: {
    type: 'ams',
    plural: 'ams',
    classID: 'model.accessory.ams',
    // cms_accessory.get('ams') — same board cms_concrete_accessory doc as the
    // other types; no per-type params route exists for ams (form reads the
    // doc handed in by tab.accessories).
    catalogueFields: [],
    subtabs: ['properties', 'mounting', 'remarks'],
    withPosition: true,
    setupMounting: { withPopup: true },
    validatedFields: [],
    hasAutoPosition: false,
  },

  user_defined_accessory: {
    type: 'user_defined_accessory',
    plural: 'user_defined_accessories',
    classID: 'model.accessory.user_defined_accessory',
    // no cms_accessory (map.js current.cms.user_defined_accessory commented out).
    catalogueFields: [],
    // form.js: ['properties','electrical','priceing','performance','remarks']
    // ('priceing' [sic] is a dead config key — the tmpl input is 'pricing').
    subtabs: ['properties', 'electrical', 'priceing', 'performance', 'remarks'],
    withPosition: false,
    validatedFields: [],
    hasAutoPosition: false,
  },
};

/** UDA master collections (user_defined_accessory.params.js:7-23). */
export const UDA_COLLECTION_URLS = {
  supplyedBy: '/api/v1.0/master/v1.0.0/ahu/supplyed_by/',
  airStreams: '/api/v1.0/master/v1.0.0/ahu/air_streams/',
  voltages: '/api/v1.0/master/v1.0.0/ahu/voltages/',
  powerBy: '/api/v1.0/master/v1.0.0/ahu/powered_by/',
  udaTypes: '/api/v1.0/master/v1.0.0/ahu/uda_types/',
  /** udc.pricing_method.js:9 (UDA pricing collection — §49.6 boot). */
  udaPricing: '/api/v1.0/master/v1.0.0/ahu/user_defined_component_accessory_pricing_methods/',
} as const;
